<template>
	<div :ref="fieldRef" class="display-flex" :class="{ 'flex-column': isMappingDisabled }">
		<div class="header-container display-flex flex-row flex-align-center px-3" v-if="isMappingDisabled">
			<SIcon name="setup" width="14" class="mr-2 mt-1" />
			This is a block in setup
		</div>
		<div
			class="connection-group-container display-flex flex-row width-100 flex-align-center flex-1"
			:class="{ 'px-4 py-2': isMappingDisabled }"
		>
			<div class="flex-centerize flex-column mt-7">
				<ConnectionServiceCard :serviceSlug="serviceSlug" />
				<div class="mapping-icon-container flex-centerize">
					<MappingIconSvg class="mapping-icon" />
				</div>
			</div>
			<div class="flex-1 display-flex flex-row flex-align-center flex-justify-space-between">
				<div v-if="!isMappingDisabled && isMappingSetupFinished" class="flex-1 flex-space-between--center">
					<div>
						<div class="font-size-md font-weight-600">Map your data in {{ service.service_name }}</div>
						<div class="font-size-sm soft-message ml-1">{{ mappingTitle }}</div>
						<div class="font-size-xs soft-message ml-1">{{ description }}</div>
					</div>
					<div v-if="alreadyMapped && !editMode" class="state-indicator flex-1 display-flex flex-column flex-align-end">
						<div class="connection-state display-flex flex-row flex-align-center">
							<SIcon class="connection-status-icon mr-1" name="successfull-connection" /> Mapped
						</div>
						<div class="connection-state display-flex flex-row flex-align-center">
							<div class="link-style" @click="openMapper">Edit mapping</div>
						</div>
					</div>
					<AppTooltip v-else :disabled="!mapDataButtonDisabled" top :text="tooltipText">
						<OutlinedButton :disabled="mapDataButtonDisabled" @click="openMapper" class="mapping-button"> Map Data </OutlinedButton>
					</AppTooltip>
				</div>

				<div class="flex-1 flex-space-between--center" v-else>
					<div>
						<div class="font-size-md font-weight-600">Map your {{ service.service_name }} data</div>
						<div class="font-size-sm soft-message ml-1">{{ mappingTitle }}</div>
						<div class="font-size-sm soft-message">{{ orderedDependencyLabels(' > ') }}</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { pluralize } from '@/utils/language-service';
import { configurationsValuesByFieldUUID } from '@/store/modules/configurationEditor/getters';
import { getConfigurationFieldResponse, getConfigurationFieldsIdsPerInterface } from '@/store/modules/workflowConfigurations/getters';
import { getService } from '@/store/modules/apps/getters';
import { userSpecificConfigurationId, generalConfigurationId } from '@/store/modules/selectedWorkflow/modules/configurations/getters';
import ConnectionServiceCard from '@/views/Flow/configurations/SetupMenu/CredentialsSelector/ConnectionServiceCard';

import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import DynamicDataService from '@/utils/dynamic-data-service';

import WorkflowConfigurationService from '@/utils/workflow-configuration-service';
import AppTooltip from '@/components/common/AppTooltip';
import WidgetFieldMixin from './WidgetFieldMixin';
import IsRequiredFieldComputedMixin from './mixins/IsRequiredFieldComputedMixin';

import MappingIconSvg from '../../dialogs/DynamicDataMappingDialog/assets/MappingIconSvg';

import { CredentialsTypes } from '@ply-io/ply-front-shared-utilities';

export default {
	name: 'UserSchemaMappingsSelectorField',
	props: {
		// action: { type: Object },
		editMode: { type: Boolean, default: false },
		isMappingDisabled: { type: Boolean, default: false },
		// actionParams: { type: Array },
		// configurationId: { type: Number },
	},
	mixins: [WidgetFieldMixin, IsRequiredFieldComputedMixin],
	components: {
		AppTooltip,
		ConnectionServiceCard,
		MappingIconSvg,
	},
	methods: {
		async openMapper() {
			const { configurationInterfaceUUID, field } = this;
			await AppDialogManagerService.openUserSchemaMappingRespondDialog({
				configurationInterfaceUUID,
				widgetFieldUUID: field.id,
				service: this.service,
				fieldItem: this.widgetFieldMetadataSource.field_item,
				fieldTitle: this.mappingTitle,
			});
		},
		orderedDependencyLabels(delimiter = ',') {
			return this.orderedDependencyLabelsArray.join(delimiter) || 'Not mapped';
		},
	},
	computed: {
		...mapGetters([
			getService.fullPath,
			getConfigurationFieldResponse.fullPath,
			getConfigurationFieldsIdsPerInterface.fullPath,
			configurationsValuesByFieldUUID.fullPath,
		]),
		...mapGetters([generalConfigurationId.fullPath, userSpecificConfigurationId.fullPath]),
		mapDataButtonDisabled() {
			return !this.relatedCredentialsPickerIsFilled || this.editMode;
		},
		tooltipText() {
			if (this.editMode) {
				return `This button can only be clicked in test mode`;
			} else if (!this.relatedCredentialsPickerIsFilled) {
				return `Select account connection first`;
			}
			return '';
		},
		serviceSlug() {
			return this.widgetFieldMetadataSource.service_slug || this.service?.service_slug;
		},
		serviceTitle() {
			const additionalTitleObj = this.field.metadata.options.additional_title || {};
			const additionalTitle = additionalTitleObj.enabled && additionalTitleObj.value ? ' - ' + additionalTitleObj.value : '';
			return (this.widgetFieldMetadataSource?.service_name || this.service?.service_name || '') + additionalTitle || '';
		},
		mappingTitle() {
			return this.widgetFieldMetadataSource.field_title;
		},
		widgetFieldMetadataSource() {
			return this.field.metadata.source || {};
		},
		relatedCredentialsPickerIsFilled() {
			const relatedCredsPickerUUID = this.widgetFieldMetadataSource?.credentials_configuration?.source_field_uuid;
			return !!relatedCredsPickerUUID && !!this[configurationsValuesByFieldUUID.fullPath][relatedCredsPickerUUID];
		},
		orderedDependencyLabelsArray() {
			const paramsMapDataSource = this.fieldValue || this.fieldResponse;
			return Object.values(paramsMapDataSource?.params_map_labels || []);
		},
		alreadyMapped() {
			const mappingResponse = this[configurationsValuesByFieldUUID.fullPath][this.field?.id];
			const paramsDefinition = this.service.param_mappings?.[this.widgetFieldMetadataSource.field_item].params || [];
			return mappingResponse && DynamicDataService.isValidMappingResponse(paramsDefinition, mappingResponse.params_map);
		},
		service() {
			return this[getService.fullPath]([this.field.metadata.source.service]) || {};
		},
		configurationInterfaceUUID() {
			return WorkflowConfigurationService.getConfigurationInterfaceFromField(
				this.configurationId,
				this.field.id,
				this[getConfigurationFieldsIdsPerInterface.fullPath]
			);
		},
		fieldResponse() {
			return this[getConfigurationFieldResponse.fullPath](this.configurationId, this.field.id);
		},
		isMappingSetupFinished() {
			return DynamicDataService.isMappingSetupFinished(this.field, this.service);
		},
		numOfMappings() {
			const mappings = this.fieldValue?.mappings || [];
			return Object.values(mappings).length;
		},
		description() {
			const { numOfMappings, orderedDependencyLabels } = this;
			return numOfMappings === 0 ? 'No mappings yet' : `${pluralize('mapping', numOfMappings, true)} in ${orderedDependencyLabels(',')}`;
		},
		configurationId() {
			return this.widgetFieldMetadataSource.credentials_configuration.credentials_type === CredentialsTypes.ProfileBased
				? this[userSpecificConfigurationId.fullPath]
				: this[generalConfigurationId.fullPath]; // CredentialsTypes.AccountBased
		},
	},
};
</script>

<style lang="scss" scoped>
.header-container {
	font-size: 12px;
	color: #8c95a1;
	font-weight: medium;

	height: 30px;
	border-radius: 10px 10px 0px 0px;
	border-bottom: 1px solid var(--border-color);
	background: var(--setup-node-container-background);
}
.connection-group-container {
	height: 67px;
	width: 100%;
}

::v-deep(.v-btn) {
	&.mapping-button {
		height: 35px !important;
		padding: 0px !important;
		font-size: 14px !important;
		width: 97px !important;
		border-radius: 8px !important;
		border: 1px solid var(--bold-border-color) !important;
		color: var(--bold-border-color) !important;

		&:disabled {
			border: none !important;
			color: #adadad !important;
			background: #e0e0e0 !important;
		}
	}
}
.mapping-icon {
	height: 12px;
}
.mapping-icon-container {
	top: -54px;
	left: 12px;
	width: 22px;
	height: 22px;
	border-radius: 11px;
	background: var(--bg-color);
	position: relative !important;
	border: 1px solid var(--border-color);
}

.state-indicator {
	.connection-state {
		font-size: 14px;
		color: var(--primary-font-color);
	}
}

.soft-message {
	margin: 2px 0 -7px -3px;
}
</style>
